import React from "react";
import { Search, Play, Pause, Square, RotateCcw, Users, ChevronLeft, ChevronRight } from "lucide-react";

/*
 * Standalone, pixel-fixed renderings of the Fight Studio panels.
 * Used only to capture product imagery — not linked from the site.
 */

const Chrome = ({ children, tab }) => (
  <div className="grain relative w-[1280px] overflow-hidden border border-white/12 bg-[#0d0d11]">
    <div className="flex items-center justify-between border-b border-white/10 bg-[#111116] px-5 py-3">
      <div className="flex items-center gap-3">
        <span className="h-2 w-2 rounded-full bg-[hsl(var(--orv-blue))]" />
        <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/70">
          Orvyn Fight Studio · v4.2.0
        </span>
      </div>
      <div className="flex items-center gap-6">
        {["Library", "Lineup", "Sequence", "Characters", "Spectators"].map((t) => (
          <span
            key={t}
            className={`font-mono text-[10px] uppercase tracking-[0.2em] ${
              t === tab ? "text-white" : "text-white/30"
            }`}
          >
            {t}
          </span>
        ))}
        <span className="border border-white/12 px-2 py-1 font-mono text-[9px] tracking-[0.18em] text-white/40">
          F7
        </span>
      </div>
    </div>
    {children}
  </div>
);

const MOVES = [
  ["01", "Roundhouse — high", "Taekwondo", "Paired"],
  ["02", "Lead hand trap + straight", "Jeet Kune Do", "Paired"],
  ["03", "Chain punch flurry", "Wing Chun", "Paired"],
  ["04", "Spinning back kick", "Taekwondo", "Kick"],
  ["05", "Mount — ground and pound", "Full Mount", "Paired"],
  ["06", "Arm drag counter", "Counters", "Paired"],
  ["07", "Rear hand-to-hand takedown", "Takedowns", "Paired"],
  ["08", "Dual knife slash combo", "Dual Knives", "Weapons"],
  ["09", "Sword overhead finisher", "Sword", "Weapons"],
  ["10", "Gun fu disarm", "Gun Fu", "Paired"],
  ["11", "Guard stance — idle", "Taekwondo", "Solo"],
  ["12", "Knockout — rear fall", "Finishers", "Solo"],
];

export const LibraryShot = () => (
  <Chrome tab="Library">
    <div className="flex">
      <div className="w-[46%] border-r border-white/10 p-6">
        <div className="mb-4 flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2.5">
          <Search className="h-3.5 w-3.5 text-white/30" />
          <span className="font-mono text-[11px] tracking-[0.12em] text-white/35">
            Search 262 animations…
          </span>
        </div>
        <div className="mb-5 flex flex-wrap gap-2">
          {["All packs", "Kicks", "Punches", "Takedowns", "Grappling", "Weapons"].map((f, i) => (
            <span
              key={f}
              className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] ${
                i === 1
                  ? "border-[hsl(var(--orv-blue))]/60 bg-[hsl(var(--orv-blue))]/10 text-[hsl(var(--orv-blue))]"
                  : "border-white/10 text-white/40"
              }`}
            >
              {f}
            </span>
          ))}
        </div>
        <div className="divide-y divide-white/6">
          {MOVES.map(([n, name, pack, type], i) => (
            <div
              key={n}
              className={`flex items-center gap-3 py-2.5 ${i === 3 ? "bg-[hsl(var(--orv-blue))]/12" : ""}`}
            >
              <span
                className={`grid h-6 w-6 shrink-0 place-items-center border font-mono text-[10px] ${
                  i === 3
                    ? "border-[hsl(var(--orv-blue))] text-[hsl(var(--orv-blue))]"
                    : "border-white/12 text-white/40"
                }`}
              >
                {n}
              </span>
              <span className={`flex-1 truncate text-[13px] ${i === 3 ? "text-white" : "text-white/65"}`}>
                {name}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/25">{pack}</span>
              <span className="w-16 text-right font-mono text-[9px] uppercase tracking-[0.14em] text-white/30">
                {type}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="w-[54%]">
        <div className="relative h-[556px] overflow-hidden border-b border-white/10 bg-[#080809]">
          <div className="hairline-grid absolute inset-0 opacity-50" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_62%,rgba(59,130,246,0.12),transparent_62%)]" />
          <div className="absolute inset-x-0 top-[16%] flex justify-center gap-8 opacity-30">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <span key={i} className="h-5 w-5 rounded-full border border-white/40" />
            ))}
          </div>
          <div className="absolute left-[28%] top-[58%] -translate-y-1/2">
            <div className="grid h-16 w-16 place-items-center rounded-full border border-white/25">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/75">A</span>
            </div>
            <span className="mt-2 block text-center font-mono text-[8px] uppercase tracking-[0.2em] text-white/35">
              Attacker
            </span>
          </div>
          <div className="absolute left-[58%] top-[58%] -translate-y-1/2">
            <div className="grid h-16 w-16 place-items-center rounded-full border border-[hsl(var(--orv-blue))]/60">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--orv-blue))]">
                V
              </span>
            </div>
            <span className="mt-2 block text-center font-mono text-[8px] uppercase tracking-[0.2em] text-white/35">
              Victim
            </span>
          </div>
          <span className="absolute left-5 top-5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
            Full fight · 60s
          </span>
          <span className="absolute right-5 top-5 font-mono text-[10px] tracking-[0.18em] text-white/40">
            18.42s / 60.00s
          </span>
          <span className="absolute bottom-5 left-5 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">
            <Users className="h-3 w-3" /> Crowd · cheering
          </span>
        </div>

        <div className="p-6">
          <div className="relative mb-6 h-8">
            <div className="absolute top-1/2 h-[3px] w-full -translate-y-1/2 bg-white/10" />
            <div className="absolute top-1/2 h-[3px] w-[31%] -translate-y-1/2 bg-[hsl(var(--orv-blue))]" />
            <div className="absolute left-[31%] top-1/2 h-4 w-[2px] -translate-y-1/2 bg-white" />
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-2 bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-black">
              <Pause className="h-3 w-3 fill-current" /> Pause
            </span>
            <span className="flex items-center gap-2 border border-white/12 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60">
              <Square className="h-3 w-3 fill-current" /> Stop
            </span>
            <span className="flex items-center gap-2 border border-white/12 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60">
              <RotateCcw className="h-3 w-3" /> Replay
            </span>
            <div className="ml-auto flex items-center gap-1 border border-white/10 p-1">
              {["0.25x", "0.5x", "1x", "1.5x"].map((s, i) => (
                <span
                  key={s}
                  className={`px-2.5 py-1 font-mono text-[10px] ${
                    i === 2 ? "bg-white text-black" : "text-white/45"
                  }`}
                >
                  {s}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1 border border-white/10 p-1">
              {["Now", "3s", "5s", "10s"].map((s, i) => (
                <span
                  key={s}
                  className={`px-2.5 py-1 font-mono text-[10px] ${
                    i === 1 ? "bg-white text-black" : "text-white/45"
                  }`}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Chrome>
);

const Slider = ({ label, value, pct }) => (
  <div className="py-2.5">
    <div className="flex items-baseline justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">{label}</span>
      <span className="font-mono text-[10px] text-white/30">{value}</span>
    </div>
    <div className="relative mt-2 h-[3px] w-full bg-white/10">
      <div className="absolute inset-y-0 left-0 bg-[hsl(var(--orv-blue))]" style={{ width: pct }} />
      <div className="absolute top-1/2 h-3 w-[2px] -translate-y-1/2 bg-white" style={{ left: pct }} />
    </div>
  </div>
);

const Stepper = ({ label, value }) => (
  <div className="flex items-center justify-between border border-white/10 px-3 py-2">
    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">{label}</span>
    <span className="flex items-center gap-3">
      <ChevronLeft className="h-3 w-3 text-white/35" />
      <span className="w-8 text-center font-mono text-[11px] text-white/80">{value}</span>
      <ChevronRight className="h-3 w-3 text-white/35" />
    </span>
  </div>
);

export const EditorShot = () => (
  <Chrome tab="Characters">
    <div className="flex">
      <div className="w-[30%] border-r border-white/10 p-6">
        <div className="label">Model</div>
        <div className="mt-3 border border-white/12 bg-black/40 px-3 py-2.5 font-mono text-[11px] text-white/70">
          mp_f_freemode_01
        </div>
        <div className="mt-6 space-y-2">
          <Slider label="Nose width" value="0.34" pct="62%" />
          <Slider label="Cheek bones" value="-0.12" pct="42%" />
          <Slider label="Jaw width" value="0.58" pct="74%" />
          <Slider label="Chin length" value="0.05" pct="52%" />
          <Slider label="Skin blend" value="0.70" pct="70%" />
        </div>
        <div className="mt-7 label">Hair & makeup</div>
        <div className="mt-3 space-y-2">
          <Stepper label="Hair" value="12" />
          <Stepper label="Colour" value="04" />
          <Stepper label="Eyebrows" value="07" />
        </div>
      </div>

      <div className="w-[42%]">
        <div className="relative h-[560px] overflow-hidden bg-[#080809]">
          <div className="hairline-grid absolute inset-0 opacity-40" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(255,255,255,0.07),transparent_60%)]" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="mx-auto h-16 w-16 rounded-full border border-white/25 bg-white/[0.05]" />
            <div className="mx-auto mt-2 h-2 w-8 bg-white/10" />
            <div className="mx-auto mt-1 h-[150px] w-[104px] rounded-t-[22px] border border-white/22 bg-white/[0.04]" />
            <div className="mt-1 flex justify-center gap-2">
              <div className="h-[130px] w-[42px] rounded-b-[10px] border border-white/18 bg-white/[0.03]" />
              <div className="h-[130px] w-[42px] rounded-b-[10px] border border-white/18 bg-white/[0.03]" />
            </div>
            <div className="mx-auto mt-4 h-[3px] w-32 rounded-full bg-white/10 blur-[1px]" />
          </div>
          <div className="absolute left-5 top-5 flex gap-2">
            {["Face", "Body", "Legs"].map((v, i) => (
              <span
                key={v}
                className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] ${
                  i === 0 ? "border-white/60 bg-white/10 text-white" : "border-white/12 text-white/40"
                }`}
              >
                {v}
              </span>
            ))}
          </div>
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/25">
            Drag to rotate · scroll to zoom
          </span>
        </div>
      </div>

      <div className="w-[28%] border-l border-white/10 p-6">
        <div className="label">Clothing</div>
        <div className="mt-3 space-y-2">
          <Stepper label="Tops" value="41" />
          <Stepper label="Undershirt" value="15" />
          <Stepper label="Legs" value="23" />
          <Stepper label="Shoes" value="08" />
          <Stepper label="Accessory" value="None" />
        </div>

        <div className="mt-7 label">Gi presets</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {["White", "Red", "Blue", "Black & yellow", "Black belt", "Sleeveless"].map((g, i) => (
            <span
              key={g}
              className={`border px-2 py-2 text-center font-mono text-[9px] uppercase tracking-[0.12em] ${
                i === 0
                  ? "border-[hsl(var(--orv-blue))]/60 bg-[hsl(var(--orv-blue))]/10 text-[hsl(var(--orv-blue))]"
                  : "border-white/10 text-white/45"
              }`}
            >
              {g}
            </span>
          ))}
        </div>

        <div className="mt-7 label">Saved roster</div>
        <div className="mt-3 divide-y divide-white/6">
          {["Sensei — black belt", "Kaito", "Mira", "Street fighter 02"].map((c, i) => (
            <div key={c} className="flex items-center justify-between py-2.5">
              <span className={`text-[12px] ${i === 0 ? "text-white" : "text-white/55"}`}>{c}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/25">
                {i === 0 ? "Attacker" : "Assign"}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-7 flex gap-2">
          <span className="flex-1 bg-white py-2.5 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-black">
            Save
          </span>
          <span className="flex-1 border border-white/12 py-2.5 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">
            Cancel
          </span>
        </div>
      </div>
    </div>
  </Chrome>
);

export const SequenceShot = () => (
  <Chrome tab="Sequence">
    <div className="flex">
      <div className="w-[52%] border-r border-white/10 p-6">
        <div className="flex items-baseline justify-between">
          <div className="label">Your sequence</div>
          <span className="font-mono text-[10px] tracking-[0.14em] text-white/30">
            8 moves · 42.5s · KO on
          </span>
        </div>
        <div className="mt-5 space-y-2">
          {[
            ["01", "Guard stance — idle", "Solo"],
            ["02", "Chain punch flurry", "Paired"],
            ["03", "Roundhouse — high", "Paired"],
            ["04", "Arm drag counter", "Paired"],
            ["05", "Rear hand-to-hand takedown", "Paired"],
            ["06", "Mount — ground and pound", "Paired"],
            ["07", "Spinning back kick", "Kick"],
            ["08", "Knockout — rear fall", "Solo"],
          ].map(([n, name, type], i) => (
            <div
              key={n}
              className={`flex items-center gap-3 border px-3 py-2.5 ${
                i === 2
                  ? "border-[hsl(var(--orv-blue))]/50 bg-[hsl(var(--orv-blue))]/10"
                  : "border-white/10"
              }`}
            >
              <span
                className={`grid h-7 w-7 shrink-0 place-items-center border font-mono text-[10px] ${
                  i === 2
                    ? "border-[hsl(var(--orv-blue))] text-[hsl(var(--orv-blue))]"
                    : "border-white/12 text-white/40"
                }`}
              >
                {n}
              </span>
              <span className={`flex-1 text-[13px] ${i === 2 ? "text-white" : "text-white/65"}`}>
                {name}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/25">
                {type}
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="font-mono text-[8px] text-white/30">▲</span>
                <span className="font-mono text-[8px] text-white/30">▼</span>
              </span>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center gap-2">
          <span className="flex items-center gap-2 bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-black">
            <Play className="h-3 w-3 fill-current" /> Play sequence
          </span>
          <span className="border border-white/12 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60">
            Undo
          </span>
          <span className="ml-auto flex items-center gap-2 border border-[hsl(var(--orv-blue))]/45 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--orv-blue))]">
            Finishing KO
          </span>
        </div>
      </div>

      <div className="w-[48%] p-6">
        <div className="label">Guided lineup</div>
        <div className="relative mt-5 h-[300px] overflow-hidden border border-white/10 bg-[#080809]">
          <div className="hairline-grid absolute inset-0 opacity-50" />
          <div className="absolute left-[30%] top-1/2 -translate-y-1/2">
            <div className="grid h-14 w-14 place-items-center rounded-full border border-white/25">
              <span className="font-mono text-[9px] tracking-[0.16em] text-white/70">A</span>
            </div>
          </div>
          <div className="absolute left-[56%] top-1/2 -translate-y-1/2">
            <div className="grid h-14 w-14 place-items-center rounded-full border border-dashed border-[hsl(var(--orv-blue))]/60">
              <span className="font-mono text-[9px] tracking-[0.16em] text-[hsl(var(--orv-blue))]">V</span>
            </div>
            <span className="mt-2 block text-center font-mono text-[8px] uppercase tracking-[0.18em] text-white/30">
              Ghost preview
            </span>
          </div>
          <span className="absolute bottom-4 left-4 font-mono text-[9px] uppercase tracking-[0.18em] text-white/30">
            Aim at the floor to place · scroll to rotate
          </span>
        </div>

        <div className="mt-6 space-y-2">
          <Slider label="Distance" value="0.42 m" pct="58%" />
          <Slider label="Sideways" value="-0.08 m" pct="46%" />
          <Slider label="Height" value="0.00 m" pct="50%" />
          <Slider label="Facing" value="184°" pct="51%" />
        </div>

        <div className="mt-6 flex items-center gap-2">
          <span className="flex items-center gap-2 border border-white/12 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60">
            Preview
          </span>
          <span className="bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-black">
            Apply
          </span>
          <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.16em] text-white/25">
            Impact frame · fist → head
          </span>
        </div>
      </div>
    </div>
  </Chrome>
);

export default function UiShots() {
  return (
    <main className="bg-[#08080a] p-10">
      <div className="space-y-12">
        <div data-shot="library">
          <LibraryShot />
        </div>
        <div data-shot="editor">
          <EditorShot />
        </div>
        <div data-shot="sequence">
          <SequenceShot />
        </div>
      </div>
    </main>
  );
}
